import React, { FC, useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ModalComponent from '../../../../../components/modal/ModalComponent';
import { useIntl } from 'react-intl';
import { Formik, Form, FastField, FormikProps } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import FieldWrapper from '../../../../../components/library/inputs/fieldWrapper/FieldWrapper';
import TextInput from '../../../../../components/library/inputs/text/TextInput';
import SelectInput from '../../../../../components/library/inputs/select/SelectInput';
import DateTime from '../../../../../components/library/inputs/dateTime/DateTime';
import NumberInput from '../../../../../components/library/inputs/number/NumberInput';
import { RootState } from '../../../../../store';
import { setForm } from '../../../../../slices/formadmin';
import CheckboxInput from '../../../../../components/library/inputs/checkbox/CheckboxInput';
import useUpdateForm from '../../../../../hooks/api/form/useUpdateForm';
import usePaymentAccounts from '../../../../../hooks/api/payment/stripe/usePaymentAccounts';
import CurrencyInput from '../../../../../components/library/inputs/currency/CurrencyInput';
import OptionList from '../../../../../components/library/inputs/optionlist/OptionList';
import MultipleInput from '../../../../../components/library/inputs/multiple/MultipleInput';
import CultureTextInput from '../../../../../components/culture/CultureTextInput';
import { CultureTextModel } from '../../../../../models/form/form';
import { cultureYup } from '../../../../../utilities/CultureUtilities';
import Avatar from '../../../../../components/layout/avatar/Avatar';
import { Button } from 'react-bootstrap';

interface Props {
  closeSettings: () => void;
  show: boolean;
}

interface Values {
  formName: CultureTextModel[] | string;
  startDate: string;
  endDate: string;
  formLimit: number | undefined | null;
  recaptchaEnabled: boolean;
  paymentEnabled: boolean;
  paymentOptional: boolean;
  fixedAmount: string;
  paymentAccount: string;
  currency: 'usd' | 'cad';
  administratorsEmails: string[];
  emailContent: CultureTextModel[] | string;
}

interface ValueText {
  text: string;
  value: string;
}

const FormSettingsComponent: FC<Props> = ({ closeSettings, show }: Props) => {
  const [paymentAccountOptions, setPaymentAccountOptions] = useState<ValueText[]>([]);
  const [paymentActive, setPaymentActive] = useState(false);
  const organizationId = useSelector((state: RootState) => state.organization.organization?.id) as string;
  const { updateForm, setOrganizationId } = useUpdateForm(organizationId);
  const { paymentAccounts, isLoading, setOrganizationId: setId } = usePaymentAccounts(organizationId);
  const formAdmin = useSelector((state: RootState) => state.formAdmin.form);
  const formName = useSelector((state: RootState) => state.formAdmin.form?.name);
  const recaptchaEnabled = useSelector((state: RootState) => state.formAdmin.form?.recaptchaEnabled);
  const submissionLimit = useSelector((state: RootState) => state.formAdmin.form?.submissionLimit);
  const paymentEnabled = useSelector((state: RootState) => state.formAdmin.form?.paymentEnabled);
  const paymentAccount = useSelector((state: RootState) => state.formAdmin.form?.paymentAccount);
  const paymentOptional = useSelector((state: RootState) => state.formAdmin.form?.paymentOptional);
  const fixedAmount = useSelector((state: RootState) => state.formAdmin.form?.fixedAmount);
  const currency = useSelector((state: RootState) => state.formAdmin.form?.currency || 'usd');
  const emailContent = useSelector((state: RootState) => state.formAdmin.form?.emailContent);
  const intl = useIntl();
  const dispatch = useDispatch();
  const formRef = useRef<FormikProps<Values>>(null);
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage) as string;
  const localizations = useSelector((state: RootState) => state.formAdmin.form?.localizations) as string[];
  const [avatar, setAvatar] = useState<File>();
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (organizationId) {
      setOrganizationId(organizationId);
      setId(organizationId);
    }
    if (paymentAccounts) {
      const options = paymentAccounts
        .filter(
          (paymentAccount) => !(paymentAccount.type.startsWith('sportspay') && !paymentAccount.stripe_publishable_key),
        )
        .map((paymentAccount) => {
          return {
            value: paymentAccount.stripe_user_id,
            text:
              (paymentAccount.account_name ? paymentAccount.account_name + ' - ' : '') + paymentAccount.stripe_user_id,
          };
        });
      setPaymentAccountOptions(options);
    }
    if (paymentEnabled) {
      setPaymentActive(true);
    }
  }, [paymentAccounts, organizationId]);

  useEffect(() => {
    let url;
    if (formAdmin?.avatar) {
      url = `${window.configs.restapi}/api/form/file/${formAdmin?.avatar}`;
    }

    setAvatarUrl(url);
  }, [formAdmin]);

  const fixedAmountString = ((fixedAmount || 0) / 100).toFixed(2).toString();

  const FormSettingsErrorMessages = Yup.object().shape({
    formName: cultureYup(
      Yup.string()
        .min(4, intl.formatMessage({ id: 'formSettings.errorMessage.short' }))
        .max(100, intl.formatMessage({ id: 'formSettings.errorMessage.long' }))
        .required(intl.formatMessage({ id: 'formSettings.errorMessage.required' })),
    ),
    startDate: Yup.string().test('is-date-valid', 'Date is not valid', (value) => {
      return !value ? true : moment(value, 'L h:mm A', true).isValid();
    }),
    endDate: Yup.string()
      .test('is-date-valid', 'Date is not valid', (value) => {
        return !value ? true : moment(value, 'L h:mm A', true).isValid();
      })
      .when(['startDate'], (startDate, schema) => {
        return schema.test('is-date-valid', 'End date should be after start date', (value) => {
          return !value ? true : moment(value).isAfter(startDate);
        });
      }),
    formLimit: Yup.number(),
    recaptchaEnabled: Yup.bool(),
    paymentEnabled: Yup.bool(),
    paymentOptional: Yup.bool(),
    administratorsEmails: Yup.array().of(
      Yup.string()
        .trim()
        .required(intl.formatMessage({ id: 'formSettings.errorMessage.required' }))
        .email(intl.formatMessage({ id: 'formSettings.errorMessage.email' })),
    ),
    emailContent: cultureYup(Yup.string()),
  });

  const handleUpload = () => {
    inputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    setAvatar(e.target.files[0]);
    const newAvatar = URL.createObjectURL(e.target.files[0]);
    setAvatarUrl(newAvatar);
  };

  return (
    <ModalComponent
      show={show}
      closeSettings={closeSettings}
      modalHeading={intl.formatMessage({ id: 'formSettings.formInput.settingsHeading' })}
      modalSize={'lg'}
      buttons={[
        {
          text: 'Cancel',
          buttonVariant: 'secondary',
          onClick: closeSettings,
        },
        {
          text: 'Save',
          buttonVariant: 'primary',
          onClick: () => formRef.current?.submitForm(),
        },
      ]}
    >
      <div className="form-settings" style={{ margin: 12 }}>
        <Formik
          validationSchema={FormSettingsErrorMessages}
          initialValues={{
            formName: formName || '',
            startDate: '',
            endDate: '',
            recaptchaEnabled: recaptchaEnabled || false,
            formLimit: submissionLimit || undefined,
            paymentEnabled: paymentEnabled || false,
            paymentOptional: paymentOptional || false,
            fixedAmount: fixedAmountString,
            paymentAccount: paymentAccount || '',
            currency: currency || 'usd',
            administratorsEmails: (emailContent || {}).emails || [],
            emailContent: (emailContent || {}).content || '',
          }}
          onSubmit={async (values: Values) => {
            if (formAdmin) {
              const updatedForm = {
                ...formAdmin,
                name: values.formName,
                submissionLimit: values.formLimit || null,
                recaptchaEnabled: values.recaptchaEnabled,
                paymentEnabled: values.paymentEnabled,
                paymentOptional: values.paymentOptional,
                fixedAmount: Number(values.fixedAmount) * 100,
                paymentAccount: values.paymentAccount,
                paymentModes: values.paymentEnabled ? formAdmin.paymentModes : null,
                currency: values.currency,
                emailContent: {
                  emails: values.administratorsEmails,
                  content: values.emailContent,
                },
              };
              const result = await updateForm(updatedForm, avatar as File);
              if (result != null) {
                updatedForm.avatar = result.avatar;
              }
              dispatch(setForm(updatedForm));
            }
            closeSettings();
          }}
          innerRef={formRef}
        >
          <div className="d-flex flex-column align-items-center justify-content-center form-settings">
            <Form>
              <div className="badge-container mb-3 align-items-center justify-content-center" onClick={handleUpload}>
                <div className="position-relative">
                  <Avatar src={avatarUrl} width={84} height={84}></Avatar>{' '}
                  <Button className="badge" variant="primary" size="sm">
                    <span className="mdi mdi-pencil">
                      <input ref={inputRef} onChange={handleAvatarChange} className="file-input d-none" type="file" />
                    </span>
                  </Button>
                </div>
              </div>
              <FastField name={'formName'}>
                {({ meta, field, form }) => {
                  return (
                    <FieldWrapper
                      error={meta.error}
                      touched={meta.touched}
                      hiddenText={false}
                      textColor={undefined}
                      required={false}
                      label={intl.formatMessage({ id: 'formSettings.formInput.formName' })}
                      visible={true}
                      size={12}
                      name="value"
                    >
                      <CultureTextInput
                        localizations={localizations}
                        culture={selectedLanguage}
                        id="formName"
                        onChange={(val: CultureTextModel[]) => form.setFieldValue(field.name, val, true)}
                        onBlur={field.onBlur}
                        enabled={true}
                        placeholder={intl.formatMessage({ id: 'formSettings.formInput.placeholder' })}
                        value={field.value}
                        touched={meta.touched}
                        error={meta.error}
                      />
                    </FieldWrapper>
                  );
                }}
              </FastField>
              <div className="d-flex flex-row align-items-center justify-content-center">
                <FastField name={'startDate'}>
                  {(fieldProps) => {
                    return (
                      <FieldWrapper
                        error={fieldProps.meta.error}
                        touched={fieldProps.meta.touched}
                        hiddenText={false}
                        textColor={undefined}
                        required={false}
                        label={intl.formatMessage({ id: 'formSettings.formInput.startDate' })}
                        visible={true}
                        size={6}
                      >
                        <DateTime
                          enabled={true}
                          placeholder={''}
                          mode="datetime"
                          value={fieldProps.field.value}
                          error={fieldProps.meta.error}
                          touched={fieldProps.meta.touched}
                          onClose={() => fieldProps.form.setFieldTouched(fieldProps.field.name, true, true)}
                          onChange={(val: string) => fieldProps.form.setFieldValue(fieldProps.field.name, val, true)}
                        />
                      </FieldWrapper>
                    );
                  }}
                </FastField>
                <FastField name={'endDate'}>
                  {(fieldProps) => {
                    return (
                      <FieldWrapper
                        error={fieldProps.meta.error}
                        touched={fieldProps.meta.touched}
                        hiddenText={false}
                        textColor={undefined}
                        required={false}
                        label={intl.formatMessage({ id: 'formSettings.formInput.endDate' })}
                        visible={true}
                        size={6}
                      >
                        <DateTime
                          enabled={true}
                          placeholder={''}
                          mode="datetime"
                          value={fieldProps.field.value}
                          error={fieldProps.meta.error}
                          touched={fieldProps.meta.touched}
                          onClose={() => fieldProps.form.setFieldTouched(fieldProps.field.name, true, true)}
                          onChange={(val: string) => fieldProps.form.setFieldValue(fieldProps.field.name, val, true)}
                        />
                      </FieldWrapper>
                    );
                  }}
                </FastField>
              </div>
              <FastField name={'formLimit'}>
                {({ meta, field }) => {
                  return (
                    <FieldWrapper
                      error={meta.error}
                      touched={meta.touched}
                      hiddenText={false}
                      textColor={undefined}
                      required={false}
                      label={intl.formatMessage({ id: 'formSettings.formInput.limit' })}
                      visible={true}
                      size={12}
                    >
                      <NumberInput
                        id="formLimit"
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        enabled={true}
                        value={field.value}
                        touched={meta.touched}
                        error={meta.error}
                        minValue={0}
                      />
                    </FieldWrapper>
                  );
                }}
              </FastField>
              <FastField name={'recaptchaEnabled'}>
                {({ meta, field }) => {
                  return (
                    <FieldWrapper
                      error={meta.error}
                      touched={meta.touched}
                      hiddenText={true}
                      textColor={undefined}
                      required={false}
                      label=""
                      visible={true}
                      size={12}
                    >
                      <div className="property-checkbox">
                        <CheckboxInput
                          id="recaptchaEnabled"
                          onChange={field.onChange}
                          enabled={true}
                          value={field.value}
                          label={intl.formatMessage({ id: 'formSettings.formInput.captchaEnabled' })}
                        />
                      </div>
                    </FieldWrapper>
                  );
                }}
              </FastField>
              <FastField name={'paymentEnabled'}>
                {({ meta, field }) => {
                  return (
                    <FieldWrapper
                      error={meta.error}
                      touched={meta.touched}
                      hiddenText={true}
                      textColor={undefined}
                      required={false}
                      label=""
                      visible={true}
                      size={12}
                    >
                      <div className="property-checkbox">
                        <CheckboxInput
                          id="paymentEnabled"
                          onChange={(e) => {
                            field.onChange(e);
                            setPaymentActive(e.target.checked);
                          }}
                          enabled={true}
                          value={field.value}
                          label={intl.formatMessage({ id: 'formSettings.formInput.enablePayment' })}
                        />
                      </div>
                    </FieldWrapper>
                  );
                }}
              </FastField>
              {paymentActive && (
                <FastField name={'paymentOptional'}>
                  {({ meta, field }) => {
                    return (
                      <div className="d-flex flex-column payment-optional">
                        <FieldWrapper
                          error={meta.error}
                          touched={meta.touched}
                          hiddenText={true}
                          textColor={undefined}
                          required={false}
                          label=""
                          visible={true}
                          size={12}
                        >
                          <div className="property-checkbox">
                            <CheckboxInput
                              id="paymentOptional"
                              onChange={field.onChange}
                              enabled={true}
                              value={field.value}
                              label={intl.formatMessage({ id: 'formSettings.formInput.enableOtherPayment' })}
                            />
                          </div>
                        </FieldWrapper>
                      </div>
                    );
                  }}
                </FastField>
              )}
              <FastField name={'fixedAmount'}>
                {({ meta, field }) => {
                  return (
                    <div>
                      <FieldWrapper
                        error={meta.error}
                        touched={meta.touched}
                        hiddenText={false}
                        textColor={undefined}
                        required={false}
                        label={intl.formatMessage({ id: 'formSettings.formInput.fixedAmountLabel' })}
                        visible={true}
                        size={12}
                      >
                        <CurrencyInput
                          id="fixedAmount"
                          value={field.value}
                          enabled={true}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder={intl.formatMessage({ id: 'formSettings.formInput.fixedAmount' })}
                        ></CurrencyInput>
                      </FieldWrapper>
                    </div>
                  );
                }}
              </FastField>
              <FastField name={'currency'}>
                {({ meta, field, form }) => {
                  return (
                    <div>
                      <FieldWrapper
                        error={meta.error}
                        touched={meta.touched}
                        hiddenText={false}
                        textColor={undefined}
                        required={false}
                        label={'Currency'}
                        visible={true}
                        size={12}
                      >
                        <div className="d-flex justify-content-center">
                          <OptionList
                            value={[field.value]}
                            options={[
                              { text: 'USD', value: 'usd' },
                              { text: 'CAD', value: 'cad' },
                            ]}
                            onChange={(values) => {
                              if (values[0]) {
                                form.setFieldValue(field.name, values[0]);
                              }
                            }}
                            multiSelection={false}
                            className="mx-2"
                          ></OptionList>
                        </div>
                      </FieldWrapper>
                    </div>
                  );
                }}
              </FastField>

              {!isLoading && (
                <FastField name={'paymentAccount'}>
                  {({ meta, field }) => {
                    return (
                      <FieldWrapper
                        error={meta.error}
                        touched={meta.touched}
                        hiddenText={true}
                        textColor={undefined}
                        required={false}
                        label={intl.formatMessage({ id: 'formSettings.formInput.paymentAccount' })}
                        visible={true}
                        size={12}
                      >
                        <SelectInput
                          id="paymentAccount"
                          onChange={field.onChange}
                          enabled={true}
                          value={field.value}
                          options={paymentAccountOptions}
                          placeholder={intl.formatMessage({ id: 'formSettings.formInput.paymentAccount' })}
                        />
                      </FieldWrapper>
                    );
                  }}
                </FastField>
              )}

              <FastField name={'administratorsEmails'}>
                {({ meta, field, form }) => {
                  return (
                    <MultipleInput
                      values={field.value}
                      onChange={(values) => {
                        form.setFieldValue(field.name, values);
                      }}
                      error={meta.error}
                      touched={meta.touched}
                      required={false}
                      label={intl.formatMessage({ id: 'formSettings.formInput.administratorsEmails' })}
                    >
                      {({ onChange, onBlur, value, index }) => (
                        <TextInput
                          id={`administratorsEmails.${index}`}
                          onChange={(e) => onChange(e.target.value)}
                          onBlur={(e) => onBlur(e.target.value)}
                          enabled={true}
                          placeholder={''}
                          value={value}
                          touched={meta.touched}
                          error={meta.error ? meta.error[index] : null}
                        />
                      )}
                    </MultipleInput>
                  );
                }}
              </FastField>

              <FastField name={'emailContent'}>
                {({ meta, field, form }) => {
                  return (
                    <FieldWrapper
                      error={meta.error}
                      touched={meta.touched}
                      hiddenText={false}
                      textColor={undefined}
                      required={false}
                      label={intl.formatMessage({ id: 'formSettings.formInput.emailContent' })}
                      visible={true}
                      size={12}
                    >
                      <CultureTextInput
                        localizations={localizations}
                        culture={selectedLanguage}
                        id="emailContent"
                        onChange={(val: CultureTextModel[]) => form.setFieldValue(field.name, val, true)}
                        onBlur={field.onBlur}
                        enabled={true}
                        placeholder={''}
                        value={field.value}
                        touched={meta.touched}
                        error={meta.error}
                        rest={{
                          rows: 5,
                          as: 'textarea',
                        }}
                      />
                    </FieldWrapper>
                  );
                }}
              </FastField>
            </Form>
          </div>
        </Formik>
      </div>
    </ModalComponent>
  );
};

export default FormSettingsComponent;
